import { useEffect, useMemo, useState } from 'react';
import { Lock, Users, X } from 'lucide-react';
import type { Assignee } from '@cg-projects/shared';
import { Icon } from '@/core/ui/icons';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useLookupStore } from '@/features/lookups/store/lookupStore';
import { useAuthStore } from '@/features/auth/store/authStore';
import { initials, avatarColor } from '@/core/ui/helpers';

export function CreateProjectModal({ onClose }: { onClose: () => void }) {
  const createArea = useAreaStore((s) => s.createArea);
  const lookup = useLookupStore();
  const loadLookups = useLookupStore((s) => s.load);
  const user = useAuthStore((s) => s.user);

  const [name, setName] = useState('');
  const [members, setMembers] = useState<Assignee[]>([]);
  const [visibility, setVisibility] = useState<'private' | 'shared'>('shared');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void loadLookups(); }, [loadLookups]);

  const available = useMemo(
    () => lookup.assignees.filter((a) => a.id !== user?.uid && !members.some((m) => m.id === a.id)),
    [lookup.assignees, members, user?.uid],
  );

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      // Private = no members at all. Privacy is already how access works (explicit
      // membership only) — this just makes it an intentional choice instead of a
      // side effect of not adding anyone.
      await createArea(name.trim(), visibility === 'private' ? [] : members.map((m) => m.id));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create project.');
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 animate-fade-in" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 z-50 w-[460px] max-w-[94vw] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-line bg-canvas shadow-xl animate-fade-in">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <div className="text-sm font-semibold text-ink">New project</div>
          <button onClick={onClose} className="rounded p-1 text-muted hover:bg-hover hover:text-ink"><Icon i={X} size={14} /></button>
        </div>

        <div className="px-4 py-4">
          <label className="mb-1 block text-xs font-medium text-muted">Project name</label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
            placeholder="e.g. Summer Pop-Up Event"
            className="mb-4 w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm text-ink outline-none placeholder:text-faint focus:border-brand"
          />
          {/* HR names get told the policy at the door (Pat, 7/27): hiring/onboarding/
              payroll projects hold people files and identity documents, and the server
              refuses to ever make them public. Mirror of areas.routes.ts SENSITIVE_NAME. */}
          {/\b(hr|human\s*resources?|hir(e|es|ing)|new\s*hires?|onboard(ing)?|payroll|personnel|termination)\b/i.test(name) && (
            <div className="-mt-2 mb-3 rounded-md bg-brand-soft px-3 py-2 text-[12px] text-muted">
              <Icon i={Lock} size={14} className="inline align-[-0.2em]" /> Looks HR-related — this project stays <span className="font-medium text-ink">private to its members</span>. People files and ID documents are never company-visible.
            </div>
          )}

          <label className="mb-1 block text-xs font-medium text-muted">Who can see it</label>
          <div className="mb-2 grid grid-cols-2 gap-2">
            {([['private', Lock, 'Just me', 'Nobody else is added.'], ['shared', Users, 'Shared', 'You pick who joins.']] as const).map(([v, VisIcon, label, hint]) => (
              <button
                key={v}
                onClick={() => setVisibility(v)}
                className={`rounded-md border px-2.5 py-2 text-left transition ${visibility === v ? 'border-brand bg-brand/10' : 'border-line hover:bg-hover'}`}
              >
                <span className="flex items-center gap-1 text-xs font-medium text-ink"><Icon i={VisIcon} size={14} /> {label}</span>
                <span className="block text-[11px] text-faint">{hint}</span>
              </button>
            ))}
          </div>
          {/* Never show a "Private" label without this line — a privacy promise the
              product doesn't keep is worse than no privacy control at all. */}
          <p className="mb-3 text-[11px] text-faint">
            {visibility === 'private'
              ? 'Only you can open this project — plus workspace admins, who can see every project.'
              : 'Only you and the people you add can open this project — plus workspace admins, who can see every project.'}
          </p>

          {visibility === 'shared' && (
          <>
          <label className="mb-1 block text-xs font-medium text-muted">Members</label>
          <p className="mb-2 text-xs text-faint">
            You'll be the lead. <span className="text-muted">No one is added automatically.</span>
          </p>

          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-brand/15 py-0.5 pl-1 pr-2 text-xs text-brand">
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-medium text-page" style={{ backgroundColor: 'var(--color-brand)' }}>{user ? initials(user.name) : '?'}</span>
              {user?.name ?? 'You'} · Lead
            </span>
            {members.map((m) => (
              <span key={m.id} className="inline-flex items-center gap-1 rounded-full bg-hover py-0.5 pl-1 pr-1.5 text-xs">
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-medium text-white" style={{ backgroundColor: avatarColor(m.name) }}>{initials(m.name)}</span>
                <span className="text-ink">{m.name}</span>
                <button onClick={() => setMembers((cur) => cur.filter((x) => x.id !== m.id))} className="text-faint hover:text-danger" title="Remove"><Icon i={X} size={14} /></button>
              </span>
            ))}
          </div>

          <select
            value=""
            onChange={(e) => { const a = lookup.assignees.find((x) => x.id === Number(e.target.value)); if (a) setMembers((cur) => [...cur, a]); }}
            className="w-full rounded-md border border-line bg-canvas px-2 py-1.5 text-sm text-ink outline-none focus:border-brand"
          >
            <option value="">+ Add a person…</option>
            {available.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          </>
          )}

          {error &&<div className="mt-3 rounded-md border border-danger/30 bg-danger/10 px-2 py-1 text-xs text-danger">{error}</div>}

          <div className="mt-5 flex justify-end gap-2">
            <button onClick={onClose} className="rounded-md border border-line px-3 py-1.5 text-sm text-muted hover:bg-hover">Cancel</button>
            <button
              onClick={() => void submit()}
              disabled={busy || !name.trim()}
              className="rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-page transition hover:brightness-110 disabled:opacity-50"
            >
              {busy ? 'Creating…' : 'Create project'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
